import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Tab, Tabs } from "react-bootstrap";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NumericFormat as NumberFormat } from "react-number-format";
import ofertasService from "../../../_services/cart/ofertas.service";
import alertService from "../../../_services/cart/alert.service";

/**
 * Qué publicaciones entran en una oferta de alcance PRODUCTOS.
 *
 * <h3>Dos solapas y no una lista con checkboxes</h3>
 * "Elegir" muestra el catálogo y "En la oferta" lo que ya está adentro. Con una sola lista, sacar
 * algo obliga a encontrarlo primero entre las quinientas publicaciones del catálogo — y lo que uno
 * quiere sacar es justamente lo que ya eligió, que es una lista corta.
 *
 * <h3>"Agregar los N filtrados" manda el filtro, no los ids</h3>
 * Es el botón que hace usable la pantalla: un desarmadero que quiere poner 20% a todos los
 * paragolpes de Ford filtra y toca una vez. Si viajaran los ids habría que traer las mil filas al
 * navegador para después devolverlas; con el filtro, el back resuelve el conjunto donde ya está.
 */

const TAMANIO_PAGINA = 20;

const nombreProducto = (p) =>
    [p.tipoRepuesto?.nombre, p.marca?.nombre, p.modelo?.nombre, p.version?.nombre, p.anio]
        .filter(Boolean)
        .join(" · ");

export const SelectorProductos = ({ oferta, filtros, onClose, onCambio }) => {
    const [solapa, setSolapa] = useState("elegir");

    const [texto, setTexto] = useState("");
    const [marcaId, setMarcaId] = useState(null);
    const [modeloId, setModeloId] = useState(null);
    const [tipoRepuestoId, setTipoRepuestoId] = useState(null);

    const [pagina, setPagina] = useState(0);
    const [resultado, setResultado] = useState({ content: [], totalElements: 0, totalPages: 0 });
    const [seleccionados, setSeleccionados] = useState([]);
    const [marcados, setMarcados] = useState([]);
    const [dentro, setDentro] = useState({ content: [], totalElements: 0, totalPages: 0 });
    const [paginaDentro, setPaginaDentro] = useState(0);
    const [cargando, setCargando] = useState(false);
    const [total, setTotal] = useState(oferta.productos || 0);

    const modelos = useMemo(
        () => (filtros?.modelos || []).filter((m) => !marcaId || m.marca?.id === marcaId),
        [filtros, marcaId]
    );

    /** El filtro que se manda al back. Es el mismo objeto que viaja en "agregar los filtrados". */
    const armarBusqueda = useCallback(
        (page) => ({
            page,
            pageSize: TAMANIO_PAGINA,
            searchValue: texto || null,
            sortOrders: [{ field: "id", direction: "DESC" }],
            options: {
                ...(marcaId ? { marca: marcaId } : {}),
                ...(modeloId ? { modelo: modeloId } : {}),
                ...(tipoRepuestoId ? { tipoRepuesto: tipoRepuestoId } : {}),
            },
        }),
        [texto, marcaId, modeloId, tipoRepuestoId]
    );

    const buscarCandidatos = useCallback(
        (page = 0) => {
            setCargando(true);

            ofertasService
                .candidatos(oferta.id, armarBusqueda(page))
                .then((resp) => {
                    setResultado(resp.data || { content: [], totalElements: 0, totalPages: 0 });
                    setSeleccionados(resp.seleccionados || []);
                    setTotal(resp.total || 0);
                    setPagina(page);
                })
                .catch((error) => alertService.error(error))
                .finally(() => setCargando(false));
        },
        [oferta.id, armarBusqueda]
    );

    const buscarDentro = useCallback(
        (page = 0) => {
            ofertasService
                .productos(oferta.id, { page, pageSize: TAMANIO_PAGINA, sortOrders: [{ field: "id", direction: "DESC" }] })
                .then((resp) => {
                    setDentro(resp.data || { content: [], totalElements: 0, totalPages: 0 });
                    setPaginaDentro(page);
                })
                .catch((error) => alertService.error(error));
        },
        [oferta.id]
    );

    useEffect(() => {
        buscarCandidatos(0);
    }, [buscarCandidatos]);

    useEffect(() => {
        buscarDentro(0);
    }, [buscarDentro]);

    /** Refresca las dos solapas y avisa al listado, que muestra el contador de publicaciones. */
    const refrescar = () => {
        setMarcados([]);
        buscarCandidatos(pagina);
        buscarDentro(0);
        if (onCambio) onCambio();
    };

    const alternar = (id) =>
        setMarcados((m) => (m.includes(id) ? m.filter((it) => it !== id) : [...m, id]));

    const agregarMarcados = () => {
        if (marcados.length === 0) return;

        ofertasService
            .agregar(oferta.id, marcados)
            .then((resp) => {
                alertService.msg(`${resp.agregados || 0} publicaciones agregadas`, "success");
                refrescar();
            })
            .catch((error) => alertService.error(error));
    };

    const agregarTodosFiltrados = () => {
        ofertasService
            .agregarFiltrados(oferta.id, armarBusqueda(0))
            .then((resp) => {
                alertService.msg(`${resp.agregados || 0} publicaciones agregadas`, "success");
                refrescar();
            })
            .catch((error) => alertService.error(error));
    };

    const quitar = (id) => {
        ofertasService
            .quitar(oferta.id, [id])
            .then(() => refrescar())
            .catch((error) => alertService.error(error));
    };

    const quitarTodos = () => {
        ofertasService
            .quitarTodos(oferta.id)
            .then((resp) => {
                alertService.msg(`${resp.quitados || 0} publicaciones quitadas`, "success");
                refrescar();
            })
            .catch((error) => alertService.error(error));
    };

    const selectProps = {
        className: "ofertas__select",
        classNamePrefix: "ofertas__select",
        isClearable: true,
        getOptionLabel: (o) => o.nombre,
        getOptionValue: (o) => o.id,
        menuPortalTarget: document.body,
        styles: { menuPortal: (base) => ({ ...base, zIndex: 9999 }) },
    };

    const Paginador = ({ page, totalPages, onIr }) =>
        totalPages > 1 ? (
            <div className="selector__paginador">
                <button type="button" disabled={page === 0} onClick={() => onIr(page - 1)}>
                    <FontAwesomeIcon icon="fa-solid fa-chevron-left" />
                </button>
                <span>
                    {page + 1} / {totalPages}
                </span>
                <button type="button" disabled={page + 1 >= totalPages} onClick={() => onIr(page + 1)}>
                    <FontAwesomeIcon icon="fa-solid fa-chevron-right" />
                </button>
            </div>
        ) : null;

    return (
        <Modal show onHide={onClose} size="lg" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title>
                    Publicaciones de “{oferta.nombre}”
                    <small className="selector__contador">{total} adentro</small>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Tabs id="selector-productos" activeKey={solapa} onSelect={(k) => setSolapa(k)} mountOnEnter>
                    <Tab eventKey="elegir" title="Elegir del catálogo">
                        <div className="selector__filtros">
                            <label className="selector__buscador">
                                <FontAwesomeIcon icon="fa-solid fa-magnifying-glass" />
                                <input
                                    type="search"
                                    value={texto}
                                    placeholder="Buscar en la descripción"
                                    onChange={(e) => setTexto(e.target.value)}
                                />
                            </label>

                            <Select
                                {...selectProps}
                                placeholder="Marca"
                                options={filtros?.marcas || []}
                                value={(filtros?.marcas || []).find((m) => m.id === marcaId) || null}
                                onChange={(opt) => {
                                    setMarcaId(opt?.id ?? null);
                                    setModeloId(null);
                                }}
                            />

                            <Select
                                {...selectProps}
                                placeholder="Modelo"
                                options={modelos}
                                isDisabled={!marcaId}
                                value={modelos.find((m) => m.id === modeloId) || null}
                                onChange={(opt) => setModeloId(opt?.id ?? null)}
                            />

                            <Select
                                {...selectProps}
                                placeholder="Repuesto"
                                options={filtros?.tipoRepuestos || []}
                                value={(filtros?.tipoRepuestos || []).find((t) => t.id === tipoRepuestoId) || null}
                                onChange={(opt) => setTipoRepuestoId(opt?.id ?? null)}
                            />
                        </div>

                        <div className="selector__acciones">
                            <button
                                type="button"
                                className="button"
                                data-type="primary"
                                disabled={marcados.length === 0}
                                onClick={agregarMarcados}
                            >
                                Agregar {marcados.length > 0 ? `(${marcados.length})` : "seleccionadas"}
                            </button>

                            {/*
                                El atajo de la pantalla: sumar todo lo que el filtro está mostrando sin tener
                                que marcar página por página. Se ofrece sólo cuando hay más de una página,
                                que es cuando marcar a mano deja de ser razonable.
                            */}
                            {resultado.totalElements > TAMANIO_PAGINA && (
                                <button type="button" className="button" data-type="secondary" onClick={agregarTodosFiltrados}>
                                    Agregar las {resultado.totalElements} filtradas
                                </button>
                            )}
                        </div>

                        {cargando && <p className="panel__estado-carga">Cargando…</p>}

                        <ul className="selector__lista">
                            {resultado.content?.map((p) => {
                                const yaEsta = seleccionados.includes(p.id);

                                return (
                                    <li key={p.id} className={yaEsta ? "selector__item selector__item--dentro" : "selector__item"}>
                                        <label>
                                            <input
                                                type="checkbox"
                                                disabled={yaEsta}
                                                checked={yaEsta || marcados.includes(p.id)}
                                                onChange={() => alternar(p.id)}
                                            />
                                            <span className="selector__item-nombre">{nombreProducto(p)}</span>
                                        </label>

                                        <span className="selector__item-precio">
                                            <NumberFormat
                                                prefix="$"
                                                value={p.precio || 0}
                                                displayType="text"
                                                thousandSeparator="."
                                                decimalSeparator=","
                                                decimalScale={0}
                                            />
                                        </span>

                                        {yaEsta && <span className="selector__item-tag">Ya está</span>}
                                    </li>
                                );
                            })}

                            {!cargando && resultado.content?.length === 0 && (
                                <li className="selector__vacio">No hay publicaciones que coincidan con el filtro.</li>
                            )}
                        </ul>

                        <Paginador page={pagina} totalPages={resultado.totalPages || 0} onIr={buscarCandidatos} />
                    </Tab>

                    <Tab eventKey="dentro" title={`En la oferta (${total})`}>
                        {total > 0 && (
                            <div className="selector__acciones">
                                <button type="button" className="button" data-type="secondary" onClick={quitarTodos}>
                                    Quitar todas
                                </button>
                            </div>
                        )}

                        <ul className="selector__lista">
                            {dentro.content?.map((p) => (
                                <li key={p.id} className="selector__item">
                                    <span className="selector__item-nombre">{nombreProducto(p)}</span>

                                    <span className="selector__item-precio">
                                        <NumberFormat
                                            prefix="$"
                                            value={p.precio || 0}
                                            displayType="text"
                                            thousandSeparator="."
                                            decimalSeparator=","
                                            decimalScale={0}
                                        />
                                    </span>

                                    <button type="button" className="selector__quitar" onClick={() => quitar(p.id)}>
                                        <FontAwesomeIcon icon="fa-solid fa-xmark" />
                                    </button>
                                </li>
                            ))}

                            {dentro.content?.length === 0 && (
                                <li className="selector__vacio">
                                    Todavía no elegiste ninguna publicación. Mientras esté vacía, la oferta no le baja
                                    el precio a nada.
                                </li>
                            )}
                        </ul>

                        <Paginador page={paginaDentro} totalPages={dentro.totalPages || 0} onIr={buscarDentro} />
                    </Tab>
                </Tabs>
            </Modal.Body>

            <Modal.Footer>
                <button type="button" className="button" data-type="primary" onClick={onClose}>
                    Listo
                </button>
            </Modal.Footer>
        </Modal>
    );
};
